/**
 * WebSocket клиент для работы с уведомлениями Battle Pairs
 * 
 * Использует STOMP over SockJS для real-time обновлений
 * 
 * @module battlePairsWebSocket
 */

import { Client } from '@stomp/stompjs';
import { getAccessToken } from './token';

/**
 * Типы событий Battle Pairs
 * @enum {string}
 */
export const PairEventType = {
  PAIR_CREATED: 'PAIR_CREATED',
  PAIR_UPDATED: 'PAIR_UPDATED',
  PAIR_DELETED: 'PAIR_DELETED',
  PAIR_ACTIVATED: 'PAIR_ACTIVATED',
  PAIR_FINISHED: 'PAIR_FINISHED',
  PAIR_DISQUALIFIED: 'PAIR_DISQUALIFIED',
  WINNER_ANNOUNCED: 'WINNER_ANNOUNCED',
  DRAW_COMPLETED: 'DRAW_COMPLETED',
  TRACK_UPLOADED: 'TRACK_UPLOADED'
};

/**
 * Класс для управления WebSocket подключением к Battle Pairs
 */
class BattlePairsWebSocketClient {
  constructor(wsUrl = 'https://api.hrnb.ru/ws') {
    this.wsUrl = wsUrl;
    this.client = null;
    this.subscriptions = new Map(); // Map<roundId, subscription>
    this.callbacks = new Map(); // Map<roundId, callback>
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
  }

  /**
   * Подключиться к WebSocket серверу
   * @returns {Promise<void>}
   */
  async connect() {
    if (this.client && this.client.connected) {
      console.log('[BattlePairsWS] Already connected');
      return;
    }

    const token = getAccessToken();

    this.client = new Client({
      brokerURL: this.wsUrl.replace('http', 'ws'),
      connectHeaders: {
        Authorization: `Bearer ${token}`
      },
      debug: (str) => {
        console.log('[BattlePairsWS Debug]', str);
      },
      onConnect: () => {
        console.log('[BattlePairsWS] Connected');
        this.reconnectAttempts = 0;
        // Восстанавливаем подписки после переподключения
        this.restoreSubscriptions();
      },
      onStompError: (frame) => {
        console.error('[BattlePairsWS] STOMP error', frame);
      },
      onWebSocketError: (event) => {
        console.error('[BattlePairsWS] WebSocket error', event);
      },
      onDisconnect: () => {
        console.log('[BattlePairsWS] Disconnected');
        this.handleReconnect();
      }
    });

    await this.client.activate();
  }

  /**
   * Отключиться от WebSocket сервера
   */
  disconnect() {
    if (this.client) {
      // Отписываемся от всех каналов
      this.unsubscribeAll();
      this.client.deactivate();
      this.client = null;
      console.log('[BattlePairsWS] Disconnected by user');
    }
  }

  /**
   * Подписаться на уведомления раунда
   * @param {string} roundId - UUID раунда
   * @param {Function} callback - Функция обратного вызова для событий
   * @returns {Function} Функция для отписки
   */
  subscribeToRound(roundId, callback) {
    if (!this.client || !this.client.connected) {
      console.error('[BattlePairsWS] Not connected');
      return () => {};
    }

    // Если уже подписаны, обновляем callback
    if (this.callbacks.has(roundId)) {
      this.callbacks.set(roundId, callback);
      console.log('[BattlePairsWS] Updated callback for round', roundId);
      return () => this.unsubscribeFromRound(roundId);
    }

    const topicDestination = `/topic/battle_pairs.round.${roundId}`;
    
    console.log('[BattlePairsWS] Subscribing to', topicDestination);
    
    const subscription = this.client.subscribe(topicDestination, (message) => {
      try {
        const event = JSON.parse(message.body);
        console.log('[BattlePairsWS] Received event:', event);
        
        // Вызываем callback для этого раунда
        const cb = this.callbacks.get(roundId);
        if (cb) {
          cb(event);
        }
      } catch (error) {
        console.error('[BattlePairsWS] Error parsing message:', error);
      }
    });

    // Сохраняем подписку и callback
    this.subscriptions.set(roundId, subscription);
    this.callbacks.set(roundId, callback);

    // Отправляем запрос на подписку (для трекинга на сервере)
    this.client.send(`/app/battle_pairs.join.round.${roundId}`, {});

    return () => this.unsubscribeFromRound(roundId);
  }

  /**
   * Отписаться от уведомлений раунда
   * @param {string} roundId - UUID раунда
   */
  unsubscribeFromRound(roundId) {
    const subscription = this.subscriptions.get(roundId);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(roundId);
      this.callbacks.delete(roundId);
      
      // Отправляем запрос на отписку
      if (this.client && this.client.connected) {
        this.client.send(`/app/battle_pairs.leave.round.${roundId}`, {});
      }
      
      console.log('[BattlePairsWS] Unsubscribed from round', roundId);
    }
  }

  /**
   * Отписаться от всех раундов
   */
  unsubscribeAll() {
    const roundIds = Array.from(this.subscriptions.keys());
    roundIds.forEach((roundId) => {
      this.unsubscribeFromRound(roundId);
    });
  }

  /**
   * Восстановить подписки после переподключения
   */
  restoreSubscriptions() {
    const callbacksToRestore = Array.from(this.callbacks.entries());
    this.callbacks.clear();
    this.subscriptions.clear();
    
    callbacksToRestore.forEach(([roundId, callback]) => {
      this.subscribeToRound(roundId, callback);
    });
  }

  /**
   * Обработка автоматического переподключения
   */
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[BattlePairsWS] Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(
      `[BattlePairsWS] Reconnecting... Attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`
    );

    setTimeout(() => {
      this.connect();
    }, this.reconnectDelay);
  }

  /**
   * Проверка статуса подключения
   * @returns {boolean}
   */
  isConnected() {
    return this.client && this.client.connected;
  }
}

// Singleton экземпляр
let battlePairsWSClient = null;

/**
 * Получить экземпляр WebSocket клиента
 * @param {string} [wsUrl] - URL WebSocket сервера
 * @returns {BattlePairsWebSocketClient}
 */
export const getBattlePairsWSClient = (wsUrl) => {
  if (!battlePairsWSClient) {
    battlePairsWSClient = new BattlePairsWebSocketClient(wsUrl);
  }
  return battlePairsWSClient;
};

/**
 * Хук для использования в React компонентах (для функциональных компонентов)
 * 
 * @param {string} roundId - UUID раунда для подписки
 * @param {Function} onEvent - Обработчик событий
 * @returns {Object} { connected, subscribe, unsubscribe, disconnect }
 * 
 * @example
 * // В компоненте:
 * useEffect(() => {
 *   const ws = getBattlePairsWSClient();
 *   ws.connect();
 *   const unsubscribe = ws.subscribeToRound(roundId, handleEvent);
 *   return () => { unsubscribe(); ws.disconnect(); };
 * }, [roundId]);
 */
export const useBattlePairsWebSocket = (roundId, onEvent) => {
  // Это заготовка для будущего React хука
  // Реализация потребует использования useContext или создания кастомного хука
  console.warn(
    'useBattlePairsWebSocket: используйте getBattlePairsWSClient() напрямую в useEffect'
  );
  return {
    connected: battlePairsWSClient?.isConnected() ?? false,
    subscribe: () => battlePairsWSClient?.subscribeToRound(roundId, onEvent),
    unsubscribe: () => battlePairsWSClient?.unsubscribeFromRound(roundId),
    disconnect: () => battlePairsWSClient?.disconnect()
  };
};

export default {
  PairEventType,
  BattlePairsWebSocketClient,
  getBattlePairsWSClient,
  useBattlePairsWebSocket
};
